import React, { useState, createContext, useContext, useRef, useEffect } from 'react';
import { FluxTab, FluxTabBar, FluxTabPane, FluxTabs } from '@meltwater/flux-web-components-react';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import RssFeedIcon from '@mui/icons-material/RssFeed';
import BusinessIcon from '@mui/icons-material/Business';
import MediaLists from './medialists';
import Outreach from './outreach';
import Newswire from './newswire';
import NewswireOrgs from './newswireorgs';
import styles from './mediarelations.module.css';
import LandingPageLayout from '../../components/LandingPageLayout';

// Create context for sharing data between media relations components
const MediaRelationsContext = createContext();

export const MediaRelationsProvider = ({ children }) => {
    const [medialistsData, setMedialistsData] = useState(null);
    const [outreachData, setOutreachData] = useState(null);
    const [newswireData, setNewswireData] = useState(null);
    const [newswireOrgsData, setNewswireOrgsData] = useState(null);
    const [loading, setLoading] = useState(false);
    // Keep track of which components have attempted API calls
    const [apiAttempts, setApiAttempts] = useState({
        medialists: false,
        outreach: false,
        newswire: false,
        newswireorgs: false
    });

    // Track API attempts
    const markApiAttempt = (component) => {
        setApiAttempts(prev => ({
            ...prev,
            [component]: true
        }));
    };

    // Reset API attempt tracking
    const resetApiAttempt = (component) => {
        setApiAttempts(prev => ({
            ...prev,
            [component]: false
        }));
    };

    // Clear cached data for a specific component
    const clearComponentData = (component) => {
        switch (component) {
            case 'medialists':
                setMedialistsData(null);
                break;
            case 'outreach':
                setOutreachData(null);
                break;
            case 'newswire':
                setNewswireData(null);
                break;
            case 'newswireorgs':
                setNewswireOrgsData(null);
                break;
            default:
                break;
        }
    };

    const contextValue = {
        medialistsData,
        setMedialistsData,
        outreachData,
        setOutreachData,
        newswireData,
        setNewswireData,
        newswireOrgsData,
        setNewswireOrgsData,
        loading,
        setLoading,
        apiAttempts,
        markApiAttempt,
        resetApiAttempt,
        clearComponentData
    };

    return (
        <MediaRelationsContext.Provider value={contextValue}>
            {children}
        </MediaRelationsContext.Provider>
    );
};

export const useMediaRelationsData = () => useContext(MediaRelationsContext);

const MediaRelations = () => {
    const [activeTab, setActiveTab] = useState(null);
    const tabsRef = useRef(null);
    
    const handleFeatureClick = (tabId) => {
        console.log("Setting active tab from feature click:", tabId);
        setActiveTab(tabId);
    };
    
    const featureCards = [
        {
            id: "medialists",
            icon: <NewspaperIcon style={{ color: '#126e6f', fontSize: '2.5rem' }} />,
            title: "Media Lists",
            description: "View and export media lists information including contacts, permissions, and metadata. Useful for media list auditing and management.",
            buttonText: "Go to Media Lists"
        },
        {
            id: "outreach",
            icon: <MailOutlineIcon style={{ color: '#126e6f', fontSize: '2.5rem' }} />,
            title: "Outreach",
            description: "Access and manage outreach emails and recipient lists. Export data for outreach analytics and recipient list restoration.",
            buttonText: "Go to Outreach"
        },
        {
            id: "newswire",
            icon: <RssFeedIcon style={{ color: '#126e6f', fontSize: '2.5rem' }} />,
            title: "Newswire",
            description: "View and export newswire distribution data including headlines, credits used, and circuit information for audit purposes.",
            buttonText: "Go to Newswire"
        },
        {
            id: "newswireorgs",
            icon: <BusinessIcon style={{ color: '#126e6f', fontSize: '2.5rem' }} />,
            title: "Newswire Orgs",
            description: "Manage newswire organizations including export capabilities and deletion options for organization management.",
            buttonText: "Go to Newswire Orgs"
        }
    ];
    
    useEffect(() => {
        if (activeTab && tabsRef.current) {
            // Reset all tabs to inactive
            const allTabs = tabsRef.current.querySelectorAll('flux-tab');
            allTabs.forEach(tab => {
                tab.removeAttribute('active');
            });
            
            // Set the active tab
            const tabElement = tabsRef.current.querySelector(`flux-tab[id="${activeTab}"]`);
            if (tabElement) {
                tabElement.setAttribute('active', 'true');
            }
        }
    }, [activeTab]);
    
    return (
        <MediaRelationsProvider>
            <div className={styles.divStyle}>
                {!activeTab ? (
                    <LandingPageLayout
                        title="Media Relations Management"
                        description="Choose one of the options below to view or manage media relations data."
                        features={featureCards}
                        onFeatureClick={handleFeatureClick}
                        onBackClick={() => window.location.href = '/'}
                        backButtonTooltip="Home page"
                    />
                ) : (
                    <div className={styles.userContainer}>
                        <FluxTabs ref={tabsRef}>
                            <FluxTabBar 
                                onFluxActivated={(event) => {
                                    setActiveTab(event.detail.tab.id);
                                    console.log("Tab bar activated:", event.detail.tab.id);
                                }}
                            >
                                <FluxTab id="medialists" pane="medialists">
                                    Media Lists
                                </FluxTab>
                                <FluxTab id="outreach" pane="outreach">
                                    Outreach
                                </FluxTab>
                                <FluxTab id="newswire" pane="newswire">
                                    Newswire
                                </FluxTab>
                                <FluxTab id="newswireorgs" pane="newswireorgs">
                                    Newswire Orgs
                                </FluxTab>
                            </FluxTabBar>
                            <FluxTabPane name="medialists" active={activeTab === "medialists"}>
                                <div className={styles.divButtonTableContainer}>
                                    <MediaLists load={activeTab === "medialists"} setActiveTab={setActiveTab} />
                                </div>
                            </FluxTabPane>
                            <FluxTabPane name="outreach" active={activeTab === "outreach"}>
                                <div className={styles.divButtonTableContainer}>
                                    <Outreach load={activeTab === "outreach"} setActiveTab={setActiveTab} />
                                </div>
                            </FluxTabPane>
                            <FluxTabPane name="newswire" active={activeTab === "newswire"}>
                                <div className={styles.divButtonTableContainer}>
                                    <Newswire load={activeTab === "newswire"} setActiveTab={setActiveTab} />
                                </div>
                            </FluxTabPane>
                            <FluxTabPane name="newswireorgs" active={activeTab === "newswireorgs"}>
                                <div className={styles.divButtonTableContainer}>
                                    <NewswireOrgs load={activeTab === "newswireorgs"} setActiveTab={setActiveTab} />
                                </div>
                            </FluxTabPane>
                        </FluxTabs>
                    </div>
                )}
            </div>
        </MediaRelationsProvider>
    );
};

export default MediaRelations;
